import React, { Fragment } from 'react';
import { useSelector } from 'react-redux';

import { Box, Inline } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import { Platform } from 'react-native';
import analytics from '../../../modules/analytics';

import { selectors } from '../../../store/CurrentUser.model';

import { useLinkTo, useResponsiveEffect } from '../../../hooks';
import type { RootState } from '../../../store/models';
import { Button, HeaderText } from '../../ui';
import { AuthOrigins } from '../../../modules/user/constants';
import nonCriticalException from '../../../modules/exceptionLogger';
import { PROVIDER_ONBOARDING_SIGNUP, PRO_TODAY_TAB_HOME } from '../../../route-names';
import { LoginModalMode } from '../../shared/login/LoginModalProvider/LoginModalProvider';
import { useTriggerLoginModal } from '../../shared/login/LoginModalProvider';
import { SetUpMyBusinessLink } from './SetUpMyBusinessLink';

import { useShowSubscriptionFeatures } from '../../../hooks/useShowSubscriptionFeatures';

/**
 * HeaderLinks - this component renders the static links that appear in the client header
 *
 * Usage:
 * ```tsx
 * <HeaderLinksFunctional />
 * ```
 *
 * When the client user is logged in, the only link displayed will be Help
 * When the user is anonymous this bar will include links for Setup My Business,
 * Sign Up, and Log In in addition to the Help link.
 *
 * This component is connected to the redux store, and does not need any props passed
 * explicitly.
 *
 */

export const HeaderLinks: React.FCWithChildren = () => {
  const isLoggedIn: boolean = useSelector(selectors.isLoggedIn);
  const providerId: number = useSelector(selectors.getLoggedInProviderId);
  const stateName: string = useSelector((state: RootState) => state.route.name);

  const linkTo = useLinkTo();
  const is = useResponsiveEffect();
  const signupLoginProvider = useTriggerLoginModal();

  const [canShowSubscriptionFeatures] = useShowSubscriptionFeatures();

  /**
   * Opens the user signup modal
   */
  const promptUserForLoginOrSignup = async (mode: LoginModalMode = LoginModalMode.Login) => {
    analytics.track('homepage_login_link_click', { origin: 'header_nav', platform: Platform.OS });
    try {
      const { userData } = await signupLoginProvider({
        mode,
        origin: AuthOrigins.HeaderNav,
        loginRequired: true,
      });
      if (userData?.provider_id) {
        linkTo(PRO_TODAY_TAB_HOME, { providerId: userData.provider_id });
      }
    } catch (e) {
      nonCriticalException(e);
    }
  };

  let showBusinessSetup = true;

  if ((typeof stateName === 'string' && stateName.startsWith('provider_signup')) || !canShowSubscriptionFeatures) {
    showBusinessSetup = false;
  }

  const buttonStyle = {
    color: BrandColor.BackInBlack,
    margin: 'auto',
    background: BrandColor.WhiteWash,
  };

  const handleSetUpMyBusinessClicked = () => {
    analytics.track('provider_homepage_set_up_my_business_link_click', {
      origin: 'header_nav',
      platform: Platform.OS,
    });

    linkTo(PROVIDER_ONBOARDING_SIGNUP, {
      source: AuthOrigins.HeaderNav,
    });
  };

  return (
    <Inline testID="header-links-parent">
      {!isLoggedIn && (
        <Fragment>
          {!(is.mobile
          || is.mobileNarrow || is.tablet || is.desktopNarrow) && (
            <Button
              testID="header-link-login-button"
              onClick={() => promptUserForLoginOrSignup(LoginModalMode.Login)}
              style={buttonStyle}
              size="large"
            >
              <HeaderText level={3}>
                Log In
              </HeaderText>

            </Button>
          )}
          {showBusinessSetup && (
            <Box>
              <SetUpMyBusinessLink
                breakpoints={is}
                onSetUpMyBusinessClicked={handleSetUpMyBusinessClicked}
                testID="header-link-setup-my-business"
              />
            </Box>
          )}

        </Fragment>
      )}
      {!providerId && isLoggedIn && (
        <Box>
          <SetUpMyBusinessLink
            breakpoints={is}
            onSetUpMyBusinessClicked={handleSetUpMyBusinessClicked}
            testID="header-link-setup-my-business"
          />
        </Box>
      )}

    </Inline>
  );
};
